import { Component, ElementRef, EventEmitter, forwardRef, inject, Input, Output, ViewChild } from '@angular/core';
import {
  NgbCalendar,
  NgbDate,
  NgbDateAdapter,
  NgbDateParserFormatter,
  NgbDatepickerI18n,
  NgbDateStruct,
  NgbTimeStruct,
} from '@ng-bootstrap/ng-bootstrap';
import { SbDateISOParserFormatter } from '../../../services/date-time/date-formatter-iso.service';
import { SbDateStructAdapter } from '../../../services/date-time/ngb-sb-date-struct-adapter.service';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { SbDateTime, SbLocalDate } from '../../../services/api/api-date-time';
import { DateTime } from 'luxon';
import { UtilService } from '../../../services/util.service';
import { NgbTranslatedDatepicker } from '../../../services/date-time/ngb-datepicker-translated.service';
import { DateTransformationService } from '../../../services/transformation-helper/date-transformation.service';

@Component({
  selector: 'sb-date-time-range-input',
  templateUrl: './sb-date-time-range-input.component.html',
  styleUrls: ['./sb-date-time-range-input.component.scss'],
  providers: [
    { provide: NgbDatepickerI18n, useClass: NgbTranslatedDatepicker },
    { provide: NgbDateParserFormatter, useClass: SbDateISOParserFormatter },
    { provide: NgbDateAdapter, useClass: SbDateStructAdapter },
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SbDateTimeRangeInputComponent), multi: true },
  ],
  animations: [],
  standalone: false,
})
export class SbDateTimeRangeInputComponent {
  private readonly dateTransformation = inject(DateTransformationService);

  @ViewChild('datepicker') datepicker: ElementRef;

  hoveredDate: NgbDate | null = null;
  @Input() weekNumbers: boolean = true;
  @Input() showSubmit: boolean = true;
  @Input() showSeconds: boolean = false;

  visible: boolean;

  timeStructFrom: NgbTimeStruct;
  timeStructTill: NgbTimeStruct;

  dateStringFrom: string;
  dateStringTill: string;

  enableApply: boolean;

  dateStructFrom: NgbDateStruct;
  dateStructTill: NgbDateStruct;

  fullDateString: string = '';

  // tslint:disable-next-line:variable-name
  fromInput: SbDateTime;
  _from: SbDateTime;
  private initialDateEmpty: boolean;

  @Input() set from(value: SbDateTime) {
    this._from = value;
    if (value) {
      this.fromInput = value;
      this.dateStringFrom = this.dateTransformation.dateToDateString(value.value);
      this.setDate(value, 'from');
    }
  }

  get from(): SbDateTime {
    return this._from;
  }

  // tslint:disable-next-line:variable-name
  tillInput: SbDateTime;
  _till: SbDateTime;
  @Input() set till(value: SbDateTime) {
    this._till = value;
    if (value) {
      this.tillInput = value;
      this.dateStringTill = this.dateTransformation.dateToDateString(value.value);
      this.setDate(value, 'till');
    }
  }

  get till(): SbDateTime {
    return this._till;
  }

  // tslint:disable-next-line:variable-name
  _today: NgbDate;
  @Input('today')
  set today(date: NgbDate) {
    if (date === null) {
      this._today = undefined;
    } else if (date === undefined) {
      this._today = this.calendar.getToday();
    } else {
      this._today = date;
    }
  }

  get today(): NgbDate {
    return this._today;
  }

  @Output()
  dateChange: EventEmitter<{ fromDate: SbDateTime; toDate: SbDateTime }>;

  constructor(
    private readonly calendar: NgbCalendar,
    public readonly util: UtilService
  ) {
    this.initialDateEmpty = true;
    this.enableApply = false;
    this.today = this.calendar.getToday();
    this.dateChange = new EventEmitter<{ fromDate: SbDateTime; toDate: SbDateTime }>();
  }

  setDate(localDate: SbDateTime, type: string) {
    if (!localDate) {
      return;
    }
    if (type === 'from') {
      this.dateStructFrom = {
        year: localDate.value.year,
        month: localDate.value.month,
        day: localDate.value.day,
      };
      this.timeStructFrom = {
        hour: localDate.value.hour,
        minute: localDate.value.minute,
        second: this.showSeconds ? localDate.value.second : 0,
      };

      this._from = localDate;
    }
    if (type === 'till') {
      this.dateStructTill = {
        year: localDate.value.year,
        month: localDate.value.month,
        day: localDate.value.day,
      };
      this.timeStructTill = {
        hour: localDate.value.hour,
        minute: localDate.value.minute,
        second: this.showSeconds ? localDate.value.second : 59,
      };
      this._till = localDate;
    }
    this.updateFullDateString();
  }

  onDateApply() {
    this.updateFullDateString();

    const fromDt: SbDateTime = new SbDateTime();
    fromDt.value = DateTime.utc();
    if (this.dateStructFrom && this.timeStructFrom) {
      fromDt.value = fromDt.value.set({
        day: this.dateStructFrom.day,
        month: this.dateStructFrom.month,
        year: this.dateStructFrom.year,
        hour: this.timeStructFrom.hour,
        minute: this.timeStructFrom.minute,
        second: this.timeStructFrom.second,
        millisecond: 0,
      });
    }

    const tillDt: SbDateTime = new SbDateTime();
    tillDt.value = DateTime.utc();
    if (this.dateStructTill && this.timeStructTill) {
      tillDt.value = tillDt.value.set({
        day: this.dateStructTill.day,
        month: this.dateStructTill.month,
        year: this.dateStructTill.year,
        hour: this.timeStructTill.hour,
        minute: this.timeStructTill.minute,
        second: this.timeStructTill.second,
        millisecond: 0,
      });
    }

    this.dateChange.emit({ fromDate: fromDt, toDate: tillDt });
    this.visible = false;
  }

  onDateSelection(date: NgbDate) {
    if (!this.dateStructFrom && !this.dateStructTill) {
      this.dateStructFrom = date;
    } else if (this.dateStructFrom && !this.dateStructTill && (date.equals(this.dateStructFrom) || date.after(this.dateStructFrom))) {
      this.dateStructTill = date;
      if (!this.showSubmit) {
        this.onDateApply();
      }
    } else {
      this.dateStructTill = null;
      this.dateStructFrom = date;
    }
    this.isTimeOrder();
    this.updateFullDateString();
  }

  onTimePickerChanged(pickerType: string, event: NgbTimeStruct) {
    if (event) {
      if (pickerType === 'from') {
        this.timeStructFrom = event;
        this.timeStructFrom.second = this.showSeconds ? this.timeStructFrom.second : 0;
      }
      if (pickerType === 'till') {
        this.timeStructTill = event;
        this.timeStructTill.second = this.showSeconds ? this.timeStructTill.second : 59;
      }
      this.isTimeOrder();
      this.updateFullDateString();
    }
  }

  updateFullDateString() {
    let fromString: string = '';
    let tillString: string = '';
    if (this.dateStructFrom) {
      fromString = new SbLocalDate(this.dateStructFrom).toString();
      this.dateStringFrom = fromString;
    }
    if (this.dateStructTill) {
      tillString = new SbLocalDate(this.dateStructTill).toString();
      this.dateStringTill = tillString;
    }

    if (this.timeStructFrom) {
      fromString =
        (fromString !== '' ? fromString + ' ' : '') +
        this.dateTransformation.doubleDigitDisplay(this.timeStructFrom.hour) +
        ':' +
        this.dateTransformation.doubleDigitDisplay(this.timeStructFrom.minute) +
        (this.showSeconds ? ':' + this.dateTransformation.doubleDigitDisplay(this.timeStructFrom.second) : '');
    }
    if (this.timeStructTill) {
      tillString =
        (tillString !== '' ? tillString + ' ' : '') +
        this.dateTransformation.doubleDigitDisplay(this.timeStructTill.hour) +
        ':' +
        this.dateTransformation.doubleDigitDisplay(this.timeStructTill.minute) +
        (this.showSeconds ? ':' + this.dateTransformation.doubleDigitDisplay(this.timeStructTill.second) : '');
    }
    if (this.dateStructFrom && this.timeStructFrom && this.dateStructTill && this.timeStructTill) {
      this.fullDateString = fromString + (tillString ? ' - ' + tillString : '');
    }
  }

  clearDate() {
    this.dateStructFrom = null;
    this.dateStructTill = null;

    this.timeStructFrom = null;
    this.timeStructTill = null;

    this.fullDateString = '';
    this.dateChange.emit(null);
  }

  isHovered(date: NgbDate) {
    return (
      this.dateStructFrom && !this.dateStructTill && this.hoveredDate && date.after(this.dateStructFrom) && date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate) {
    return this.dateStructTill && date.after(this.dateStructFrom) && date.before(this.dateStructTill);
  }

  isRange(date: NgbDate) {
    return (
      date.equals(this.dateStructFrom) ||
      (this.dateStructTill && date.equals(this.dateStructTill)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  isFirstDayRange(date: NgbDate) {
    return date.equals(this.dateStructFrom);
  }

  isLastDayRange(date: NgbDate) {
    return date.equals(this.dateStructTill);
  }

  toggleDatepicker() {
    this.visible = !this.visible;
    if (this.visible) {
      this.isTimeOrder();
    }
    this.initialDateEmpty = this.fullDateString?.length === 0;
  }

  refreshFromDateString($event) {
    $event.stopPropagation();
    $event.preventDefault();
    if (this.fullDateString && this.fullDateString.length > 0) {
      const dateStrings = this.fullDateString.split(' - ').map(date => date.trim());
      const dates: SbDateTime[] = [];

      for (const date of dateStrings) {
        if (this.dateTransformation.parseDateFromDateString(date)) {
          dates.push(new SbDateTime(this.dateTransformation.parseDateFromDateString(date)));
        }
      }

      if (dates.length === 2) {
        this.setDate(dates[0], 'from');
        this.setDate(dates[1], 'till');
        this.isTimeOrder();
      }
    } else {
      this.clearDate();
    }

    if (this.enableApply) {
      this.onDateApply();
    } else {
      this.refreshFromInput();
    }
  }

  isTimeOrder() {
    if (this.dateStructFrom && this.dateStructTill && this.timeStructFrom && this.timeStructTill) {
      const fromDt: SbDateTime = new SbDateTime();
      fromDt.value = DateTime.utc();
      fromDt.value = fromDt.value.set({
        day: this.dateStructFrom.day,
        month: this.dateStructFrom.month,
        year: this.dateStructFrom.year,
        hour: this.timeStructFrom.hour,
        minute: this.timeStructFrom.minute,
        second: this.timeStructFrom.second,
        millisecond: 0,
      });

      const tillDt: SbDateTime = new SbDateTime();
      tillDt.value = DateTime.utc();
      tillDt.value = tillDt.value.set({
        day: this.dateStructTill.day,
        month: this.dateStructTill.month,
        year: this.dateStructTill.year,
        hour: this.timeStructTill.hour,
        minute: this.timeStructTill.minute,
        second: this.timeStructTill.second,
        millisecond: 0,
      });

      this.enableApply = fromDt < tillDt;
    } else {
      this.enableApply = false;
    }
  }

  closeDatepicker() {
    if (this.initialDateEmpty) {
      this.clearDate();
    }
    this.visible = false;
    this.refreshFromInput();
  }

  refreshFromInput() {
    this.setDate(this.fromInput, 'from');
    this.setDate(this.tillInput, 'till');
  }
}
